import { Customer, ScanConfig } from "../types/customer";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface CustomerEditModalProps {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  onSave: (customer: Customer) => void;
  isNew?: boolean;
}

const defaultConfig: ScanConfig = {
  wpscanMode: "stealth",
  disableTls: false,
  randomUserAgent: false,
  enumerateUsers: false,
  enumerateVulnerablePlugins: true,
  enumerateAllPlugins: false,
  enumerateVulnerableThemes: true,
  enumerateAllThemes: false,
  forceScan: false,
  wpscanApiToken: "",
  wpscanVulnDbEnrich: undefined,
  passwordList: "",
  proxyUrl: "",
  runNuclei: true,
  nucleiTemplatesPath: "",
  scanInterval: 7,
};

const defaultCustomer: Customer = {
  id: "",
  name: "",
  url: "",
  contact: "",
  source: "customer",
  enabled: true,
  status: "no_scans",
  config: defaultConfig,
};

export function CustomerEditModal({
  customer,
  open,
  onClose,
  onSave,
  isNew = false,
}: CustomerEditModalProps) {
  const [formData, setFormData] = useState<Customer>(customer || defaultCustomer);
  const [passwordListEnabled, setPasswordListEnabled] = useState(false);
  const { toast } = useToast();
  const initKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initKeyRef.current = null;
      return;
    }

    const key = customer ? customer.id : "new";
    if (initKeyRef.current === key) return;
    initKeyRef.current = key;

    if (customer) {
      setFormData(customer);
      setPasswordListEnabled(Boolean(customer.config.passwordList));
      return;
    }

    setFormData({ ...defaultCustomer, id: crypto.randomUUID() });
    setPasswordListEnabled(false);
  }, [open, customer, customer?.id]);

  const updateConfig = <K extends keyof ScanConfig>(key: K, value: ScanConfig[K]) => {
    setFormData((prev) => ({
      ...prev,
      config: { ...prev.config, [key]: value },
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  const passwordListPresets = ["100k-most-used-passwords-NCSC.txt", "rockyou.txt"] as const;
  const isPasswordListPreset = (
    value: string | undefined,
  ): value is (typeof passwordListPresets)[number] => {
    if (!value) return false;
    return (passwordListPresets as readonly string[]).includes(value);
  };
  const passwordListSelection = !passwordListEnabled
    ? ""
    : (
    isPasswordListPreset(formData.config.passwordList)
      ? formData.config.passwordList
      : formData.config.passwordList
        ? "custom"
        : ""
    );
  const wpscanMode = formData.config.wpscanMode ?? "stealth";
  const wpscanIsCustom = wpscanMode === "custom";
  const wpscanTokenMode = formData.config.wpscanApiToken ? "custom" : "global";
  const wpscanVulnDbMode =
    typeof formData.config.wpscanVulnDbEnrich === "boolean" ? "custom" : "global";

  return (
    <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-foreground">
            {isNew ? "Add customer" : "Edit customer"}
          </DialogTitle>
          {/* DialogContent already provides a top-right close button */}
        </DialogHeader>

        <div className="grid grid-cols-4 gap-4 mt-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="source">Tag</Label>
            <Select
              value={formData.source ?? "customer"}
              onValueChange={(value) => setFormData({ ...formData, source: value as Customer["source"] })}
            >
              <SelectTrigger id="source" className="mt-1 w-full">
                <SelectValue placeholder="Select a tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="free_scan">Free Scan (To Contact)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="url">URL</Label>
            <Input
              id="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="mt-1"
              placeholder="https://example.com"
            />
          </div>
          <div>
            <Label htmlFor="contact">Contact</Label>
            <Input
              id="contact"
              value={formData.contact}
              onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              className="mt-1"
              placeholder="email@example.com"
            />
          </div>
          <div>
            <Label htmlFor="interval">Every (days)</Label>
            <Input
              id="interval"
              type="number"
              value={formData.config.scanInterval}
              onChange={(e) => updateConfig("scanInterval", parseInt(e.target.value) || 7)}
              className="mt-1"
            />
          </div>
        </div>

        <div className="mt-6">
          <Label className="text-sm">WPScan Mode</Label>
          <div className="flex gap-2 mt-1">
            <Select
              value={wpscanMode}
              onValueChange={(value) => updateConfig("wpscanMode", value as "stealth" | "custom")}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stealth">Stealth (recommended)</SelectItem>
                <SelectItem value="custom">Custom (use options below)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Stealth runs a fixed WPScan profile with extra preflight checks; Custom enables the per-customer WPScan options.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, enabled: checked as boolean })
              }
            />
            <Label htmlFor="enabled" className="text-sm">Enabled</Label>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="disableTls"
                checked={formData.config.disableTls}
                onCheckedChange={(checked) => updateConfig("disableTls", checked === true)}
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="disableTls" className="text-sm">Disable TLS checks</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Adds --disable-tls-checks for this customer
            </p>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="randomUserAgent"
                checked={formData.config.randomUserAgent}
                onCheckedChange={(checked) => updateConfig("randomUserAgent", checked === true)}
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="randomUserAgent" className="text-sm">Random user agent</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Adds --random-user-agent for this customer
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enumerateUsers"
                checked={formData.config.enumerateUsers}
                onCheckedChange={(checked) => updateConfig("enumerateUsers", checked === true)}
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="enumerateUsers" className="text-sm">Enumerate users</Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Adds -e u</p>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enumerateVulnerablePlugins"
                checked={formData.config.enumerateVulnerablePlugins}
                onCheckedChange={(checked) =>
                  updateConfig("enumerateVulnerablePlugins", checked === true)
                }
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="enumerateVulnerablePlugins" className="text-sm">
                Enumerate vulnerable plugins
              </Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Adds -e vp</p>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enumerateAllPlugins"
                checked={formData.config.enumerateAllPlugins}
                onCheckedChange={(checked) => updateConfig("enumerateAllPlugins", checked === true)}
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="enumerateAllPlugins" className="text-sm">
                Enumerate all plugins
              </Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Adds -e ap (longer)</p>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enumerateVulnerableThemes"
                checked={formData.config.enumerateVulnerableThemes}
                onCheckedChange={(checked) =>
                  updateConfig("enumerateVulnerableThemes", checked === true)
                }
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="enumerateVulnerableThemes" className="text-sm">
                Enumerate vulnerable themes
              </Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Adds -e vt</p>
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enumerateAllThemes"
                checked={formData.config.enumerateAllThemes}
                onCheckedChange={(checked) => updateConfig("enumerateAllThemes", checked === true)}
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="enumerateAllThemes" className="text-sm">
                Enumerate all themes
              </Label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Adds -e at (longer)</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Checkbox
                id="forceScan"
                checked={formData.config.forceScan}
                onCheckedChange={(checked) => updateConfig("forceScan", checked === true)}
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="forceScan" className="text-sm">Force scan</Label>
            </div>
            <p className="text-xs text-muted-foreground">Use when WP detection fails</p>
          </div>
          <div>
            <Label className="text-sm">Password list (optional)</Label>
            <div className="mt-2 flex items-center space-x-2">
              <Checkbox
                id="passwordListEnabled"
                checked={passwordListEnabled}
                onCheckedChange={(checked) => {
                  const enabled = checked === true;
                  setPasswordListEnabled(enabled);
                  if (!enabled) {
                    updateConfig("passwordList", "");
                    return;
                  }
                  if (!formData.config.passwordList) {
                    updateConfig("passwordList", "100k-most-used-passwords-NCSC.txt");
                  }
                }}
                disabled={!wpscanIsCustom}
              />
              <Label htmlFor="passwordListEnabled" className="text-sm">
                Enable password list
              </Label>
            </div>
            <div className="flex gap-2 mt-1">
              <Select
                value={passwordListSelection}
                onValueChange={(value) => {
                  if (value === "custom") return;
                  updateConfig("passwordList", value || "");
                }}
                disabled={!wpscanIsCustom || !passwordListEnabled}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a password list" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="100k-most-used-passwords-NCSC.txt">
                    100k-most-used-passwords-NCSC.txt
                  </SelectItem>
                  <SelectItem value="rockyou.txt">rockyou.txt</SelectItem>
                  <SelectItem value="custom">Custom...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {passwordListSelection === "custom" && (
              <Input
                value={formData.config.passwordList || ""}
                onChange={(e) => updateConfig("passwordList", e.target.value)}
                placeholder="/app/wordlists/wordlist.txt"
                className="mt-2"
                disabled={!wpscanIsCustom || !passwordListEnabled}
              />
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Select or enter a path; adds --passwords
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  toast({
                    title: "Password lists",
                    description: "This demo UI does not sync lists yet (requires backend).",
                  })
                }
                disabled={!wpscanIsCustom || !passwordListEnabled}
              >
                Refresh lists
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  toast({
                    title: "Download not available",
                    description: "Downloading wordlists requires a backend or manual upload.",
                  })
                }
                disabled={!wpscanIsCustom || !passwordListEnabled}
              >
                Download NCSC 100k
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-sm">WPScan API token (optional)</Label>
            <div className="mt-2 flex gap-2">
              <Select
                value={wpscanTokenMode}
                onValueChange={(value) => {
                  if (value === "global") updateConfig("wpscanApiToken", "");
                }}
              >
                <SelectTrigger className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Use global token</SelectItem>
                  <SelectItem value="custom">Use custom token</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={formData.config.wpscanApiToken || ""}
                onChange={(e) => updateConfig("wpscanApiToken", e.target.value)}
                placeholder="Paste token here"
                type="password"
                className="flex-1"
                disabled={wpscanTokenMode !== "custom"}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Global token is set in Settings → Scanner Options. Customer token overrides global.
            </p>
          </div>
          <div>
            <Label className="text-sm">Proxy URL (optional)</Label>
            <Input
              value={formData.config.proxyUrl || ""}
              onChange={(e) => updateConfig("proxyUrl", e.target.value)}
              placeholder="http://user:pass@host:port"
              className="mt-1"
              disabled={!wpscanIsCustom}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Used for this customer only
            </p>
          </div>
          <div>
            <Label className="text-sm">WPScan VulnDB enrichment</Label>
            <div className="mt-2 flex gap-2">
              <Select
                value={wpscanVulnDbMode}
                onValueChange={(value) => {
                  if (value === "global") updateConfig("wpscanVulnDbEnrich", undefined);
                  if (value === "custom" && typeof formData.config.wpscanVulnDbEnrich !== "boolean") {
                    updateConfig("wpscanVulnDbEnrich", true);
                  }
                }}
              >
                <SelectTrigger className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Use global setting</SelectItem>
                  <SelectItem value="custom">Override</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={String(formData.config.wpscanVulnDbEnrich ?? true)}
                onValueChange={(v) => updateConfig("wpscanVulnDbEnrich", v === "true")}
                disabled={wpscanVulnDbMode !== "custom"}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Enabled</SelectItem>
                  <SelectItem value="false">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              When enabled, uses the effective WPScan API token to enrich results with known vulnerabilities.
            </p>
          </div>
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Checkbox
                id="runNuclei"
                checked={formData.config.runNuclei}
                onCheckedChange={(checked) => updateConfig("runNuclei", checked === true)}
              />
              <Label htmlFor="runNuclei" className="text-sm">Run Nuclei</Label>
            </div>
            <Input
              value={formData.config.nucleiTemplatesPath || ""}
              onChange={(e) => updateConfig("nucleiTemplatesPath", e.target.value)}
              placeholder="/app/nuclei-templates"
              disabled={!formData.config.runNuclei}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Optional templates path; leave blank to use default
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
          >
            {isNew ? "Add customer" : "Update customer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
